/* ---------------------------------------------------------------------------
   Closer Look
   Desktop: single-open pill accordion with a crossfading image stage.
   Mobile:  one of five layouts, chosen by data-mlayout on the root:
     viewer     pill rail; tapping opens the full-bleed <dialog> (original)
     inline     pill rail; tapping swaps an inline caption under the rail
     accordion  pills stack full width and expand in place
     carousel   feature cards in a swipeable snap track with dots
     pinned     the frame sticks and features advance with vertical scroll
   Desktop behaviour is identical in all five.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  function CloserLook(root) {
    this.root = root;
    var dataEl = root.querySelector('[data-hp-cl-data]');
    if (!dataEl) return;
    try { this.data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    this.pills = [].slice.call(root.querySelectorAll('.hp-cl__pill'));
    this.featurePills = this.pills.filter(function (p) { return p.hasAttribute('data-feature'); });
    this.buttons = [].slice.call(root.querySelectorAll('.hp-cl__pill-btn'));
    this.swatches = [].slice.call(root.querySelectorAll('.hp-cl__swatch'));
    this.stage = root.querySelector('.hp-cl__stage');
    this.imgs = [].slice.call(root.querySelectorAll('.hp-cl__img'));
    this.stepper = root.querySelector('.hp-cl__stepper');
    this.dot = root.querySelector('.hp-cl__dot');
    this.colorText = root.querySelector('[data-cl-color-text]');
    this.viewer = root.querySelector('.hp-cl__viewer');
    this.closeBtn = root.querySelector('[data-cl-collapse]');
    this.takeover = root.querySelector('.hp-cl__takeover');
    this.inlineHost = root.querySelector('[data-cl-inline-host]');
    this.dialogHost = root.querySelector('[data-cl-dialog-host]');
    this.mode = root.getAttribute('data-mode') || 'inline';
    this.defaultStage = root.getAttribute('data-stage-default') || 'split';

    /* mobile layout wiring */
    this.mlayout = root.getAttribute('data-mlayout') || 'viewer';
    this.mtext = root.querySelector('[data-cl-mtext]');
    this.cards = root.querySelector('[data-cl-cards]');
    this.dots = root.querySelector('[data-cl-dots]');
    this.pin = root.querySelector('[data-cl-pin]');

    this.color = 0;
    this.feature = -1;
    this.buffer = 0;

    // On mobile the Colours pill behaves like any other pill: it opens the
    // same viewer, as its own slide at the front of the sequence.
    this.viewerItems = [];
    if (this.data.colors && this.data.colors.length) this.viewerItems.push({ type: 'colors' });
    (this.data.features || []).forEach(function (f, i) { this.push({ type: 'feature', index: i }); }, this.viewerItems);

    this.bind();
    this.bindTakeover();
    this.bindCollapse();
    this.bindHint();
    this.bindCards();
    this.bindPin();
    this.setColor(0, true);

    /* Inline and pinned put one feature on screen from the start, so there is
       something to read without a tap. Carousel does not: its cards already
       carry the copy, and the stage above stays on the product colour.

       This runs at boot, while the section is usually still below the fold, so
       it must not move anything - hence silent: the pill is marked and the
       caption written, but no rail scroll and, above all, no page scroll. */
    if (this.mobile() && (this.mlayout === 'inline' || this.mlayout === 'pinned')
        && (this.data.features || []).length) {
      this.select(0, false, true);
    }
  }

  CloserLook.prototype.mobile = function () {
    return window.matchMedia('(max-width: 767px)').matches;
  };

  CloserLook.prototype.reducedMotion = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* layouts where a tap selects an item rather than opening or expanding */
  CloserLook.prototype.selectMode = function () {
    return this.mlayout === 'inline' || this.mlayout === 'carousel' || this.mlayout === 'pinned';
  };

  /* ---- horizontal-only scrollers ----------------------------------------
     scrollIntoView scrolls EVERY scrollable ancestor, the document included,
     so using it to centre a pill in its own rail also drags the page down to
     the section - on load as well as on tap. These write scrollLeft on the
     scroll container itself and can therefore never move the page vertically.
     ---------------------------------------------------------------------- */
  CloserLook.prototype.scrollXTo = function (box, el) {
    if (!box || !el) return;
    if (box.scrollWidth <= box.clientWidth + 2) return;   // nothing to scroll
    var br = box.getBoundingClientRect();
    var er = el.getBoundingClientRect();
    var left = box.scrollLeft + (er.left - br.left) - (box.clientWidth - er.width) / 2;
    var max = box.scrollWidth - box.clientWidth;
    if (left < 0) left = 0;
    if (left > max) left = max;
    if (Math.abs(left - box.scrollLeft) < 1) return;
    if (box.scrollTo) {
      box.scrollTo({ left: left, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
    } else {
      box.scrollLeft = left;
    }
  };

  /* Centres the active pill inside the rail. Never called on the boot path,
     and never in the pinned layout, where closer-look-pinned.js owns the rail
     and uses its own cached-geometry version. */
  CloserLook.prototype.railTo = function (pill) {
    if (!pill || this.mlayout === 'pinned') return;
    this.scrollXTo(this.root.querySelector('.hp-cl__pills'), pill);
  };

  /* ---- binding ---------------------------------------------------------- */
  CloserLook.prototype.bind = function () {
    var self = this;

    this.buttons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var pill = btn.closest('.hp-cl__pill');
        var isColor = pill.hasAttribute('data-colors');
        var idx = isColor ? -1 : parseInt(pill.getAttribute('data-feature'), 10);

        if (self.mobile()) {
          if (self.mlayout === 'viewer') {
            e.preventDefault();
            e.stopPropagation();
            self.openViewer(self.viewerSlotFor(isColor, idx));
            return;
          }
          if (self.selectMode()) {
            e.preventDefault();
            self.select(idx, isColor);
            return;
          }
          /* accordion falls through to the desktop expand behaviour */
        }
        self.toggle(pill, idx, isColor);
      });
    });

    this.swatches.forEach(function (sw, i) {
      sw.addEventListener('click', function () { self.setColor(i); });
      sw.addEventListener('keydown', function (e) {
        var last = self.swatches.length - 1;
        var n = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = i === last ? 0 : i + 1;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = i === 0 ? last : i - 1;
        else if (e.key === 'Home') n = 0;
        else if (e.key === 'End') n = last;
        if (n === null) return;
        e.preventDefault();
        self.setColor(n);
        self.swatches[n].focus();
      });
    });

    if (this.stepper) {
      [].slice.call(this.stepper.querySelectorAll('[data-cl-step]')).forEach(function (b) {
        b.addEventListener('click', function () {
          self.step(parseInt(b.getAttribute('data-cl-step'), 10));
        });
      });
    }

    if (this.viewer) {
      this.root.addEventListener('click', function (e) {
        var t = e.target.closest ? e.target.closest('[data-cl-viewer]') : null;
        if (!t) return;
        var act = t.getAttribute('data-cl-viewer');
        if (act === 'close') self.closeViewer();
        else self.viewerStep(act === 'next' ? 1 : -1);
      });
      this.viewer.addEventListener('close', function () {
        // returning focus must not scroll the page back down to the rail
        if (self.viewerOpener && self.viewerOpener.focus) self.viewerOpener.focus({ preventScroll: true });
      });
    }
  };

  /* ---- select (inline / carousel / pinned) -------------------------------
     Marks the active pill, swaps the stage image and writes the caption. No
     dialog, no height animation: the content is simply already there.

     silent is the boot path: state is painted but nothing scrolls at all.
     ---------------------------------------------------------------------- */
  CloserLook.prototype.select = function (idx, isColor, silent) {
    this.feature = isColor ? -1 : idx;

    this.pills.forEach(function (p) {
      var on = isColor
        ? p.hasAttribute('data-colors')
        : parseInt(p.getAttribute('data-feature'), 10) === idx;
      p.classList.toggle('is-active', !!on);
      var b = p.querySelector('.hp-cl__pill-btn');
      if (b) b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    if (!silent) {
      this.railTo(this.root.querySelector('.hp-cl__pill.is-active'));
    }

    if (this.stepper) this.stepper.hidden = false;
    this.paintMText();
    this.render();
  };

  CloserLook.prototype.paintMText = function () {
    if (!this.mtext) return;
    var html = '';
    if (this.feature >= 0) {
      var f = this.data.features[this.feature];
      if (f) html = '<strong>' + f.title + '</strong> ' + f.text;
    } else {
      var c = this.data.colors[this.color];
      if (c) html = '<strong>' + c.textTitle + '</strong> ' + c.text;
    }
    this.mtext.innerHTML = html;
    this.mtext.hidden = !html;
  };

  /* ---- mobile hint ------------------------------------------------------ */
  /* Plays once, the first time the section scrolls into view on a phone, so
     the pill row visibly reads as scrollable. */
  CloserLook.prototype.cancelHint = function () {
  this.hintDone = true;
  if (this.hintTimer) { window.clearTimeout(this.hintTimer); this.hintTimer = null; }
};

CloserLook.prototype.bindDrag = function (rail) {
  var self = this;
  var down = false, moved = false, nativeScroll = false;
  var startX = 0, startLeft = 0, pid = null;

  rail.addEventListener('pointerdown', function (e) {
    if (!self.mobile()) return;
    if (rail.scrollWidth <= rail.clientWidth + 2) return;
    self.cancelHint();
    down = true; moved = false; nativeScroll = false;
    startX = e.clientX; startLeft = rail.scrollLeft; pid = e.pointerId;
  });

  rail.addEventListener('pointermove', function (e) {
    if (!down || e.pointerId !== pid) return;
    var dx = e.clientX - startX;
    if (!moved && Math.abs(dx) < 6) return;
    // if the browser is already scrolling the rail itself, leave it alone
    if (!moved && rail.scrollLeft !== startLeft) { nativeScroll = true; down = false; return; }
    if (nativeScroll) return;
    moved = true;
    rail.classList.add('is-dragging');
    rail.scrollLeft = startLeft - dx;
  });

  var release = function () {
    down = false;
    rail.classList.remove('is-dragging');
  };
  rail.addEventListener('pointerup', release);
  // the browser claiming the gesture means native scrolling works: stand down
  rail.addEventListener('pointercancel', function () { nativeScroll = true; release(); });
  rail.addEventListener('lostpointercapture', release);

  // a drag must not also open the pill it started on
  rail.addEventListener('click', function (e) {
    if (!moved) return;
    moved = false;
    e.preventDefault();
    e.stopPropagation();
  }, true);
};

CloserLook.prototype.bindHint = function () {
  var self = this;
  var rail = this.root.querySelector('.hp-cl__pills');
  if (!rail) return;

  this.bindDrag(rail);

  var reduced = function () {
    return self.reducedMotion();
  };

  var stepAmount = function () {
    var pill = rail.querySelector('.hp-cl__pill');
    return pill ? pill.offsetWidth + 8 : Math.round(rail.clientWidth * 0.7);
  };

  var syncArrows = function () {
    if (!self.stepper || !self.mobile()) return;
    /* only the viewer layout uses the arrows to scroll the rail; the others
       step through features, so the buttons must never disable themselves */
    if (self.mlayout !== 'viewer') return;
    self.stepper.hidden = false;
    var max = rail.scrollWidth - rail.clientWidth;
    var btns = self.stepper.querySelectorAll('[data-cl-step]');
    for (var i = 0; i < btns.length; i++) {
      var dir = parseInt(btns[i].getAttribute('data-cl-step'), 10) || 0;
      btns[i].disabled = dir < 0 ? rail.scrollLeft <= 2 : rail.scrollLeft >= max - 2;
    }
  };

  // In the viewer layout a pill tap opens the dialog, so the arrows drive the
  // rail instead. Every other layout lets them step features as on desktop.
  if (this.stepper) {
    this.stepper.addEventListener('click', function (e) {
      if (!self.mobile()) return;
      if (self.mlayout !== 'viewer') return;
      var btn = e.target.closest ? e.target.closest('[data-cl-step]') : null;
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var dir = parseInt(btn.getAttribute('data-cl-step'), 10) || 1;
      rail.scrollBy({ left: dir * stepAmount(), behavior: reduced() ? 'auto' : 'smooth' });
    }, true);
  }

  rail.addEventListener('touchstart', function () { self.cancelHint(); }, { passive: true });
  rail.addEventListener('wheel', function () { self.cancelHint(); }, { passive: true });
  rail.addEventListener('scroll', syncArrows, { passive: true });
  window.addEventListener('resize', syncArrows);
  syncArrows();

  // One time nudge so the rail reads as scrollable. Scrolling rather than a CSS
  // transform, because a transform on the scroll container can swallow touch drags.
  if (this.root.getAttribute('data-mobile-hint') === 'false') return;
  if (!('IntersectionObserver' in window)) return;
  if (reduced()) return;
  var io = new IntersectionObserver(function (entries) {
    if (!entries[0].isIntersecting) return;
    io.disconnect();
    if (!self.mobile()) return;
    if (rail.scrollWidth <= rail.clientWidth + 4) return;
    if (rail.scrollLeft > 2) return;
    if (self.hintDone) return;
    var dist = parseInt(self.root.getAttribute('data-hint-distance'), 10) || 46;
    rail.scrollTo({ left: dist, behavior: 'smooth' });
    self.hintTimer = window.setTimeout(function () {
      self.hintTimer = null;
      if (self.hintDone) return;
      rail.scrollTo({ left: 0, behavior: 'smooth' });
    }, parseInt(self.root.getAttribute('data-hint-duration'), 10) || 1100);
  }, { threshold: 0.4 });
  io.observe(this.root);
};

/* ---- carousel layout ----------------------------------------------------
   The cards are plain markup rendered by Liquid, so they are readable with no
   JS at all. This only keeps the dots in step and lets a dot scroll the track.
   ------------------------------------------------------------------------ */
CloserLook.prototype.bindCards = function () {
  var self = this;
  if (!this.cards) return;
  var items = [].slice.call(this.cards.children);
  var dots = this.dots ? [].slice.call(this.dots.children) : [];
  if (!items.length) return;

  var sync = function () {
    var mid = self.cards.scrollLeft + self.cards.clientWidth / 2;
    var best = 0, bd = Infinity;
    for (var i = 0; i < items.length; i++) {
      var c = items[i].offsetLeft + items[i].offsetWidth / 2;
      var d = Math.abs(c - mid);
      if (d < bd) { bd = d; best = i; }
    }
    for (var n = 0; n < dots.length; n++) {
      dots[n].setAttribute('aria-current', n === best ? 'true' : 'false');
    }
  };

  this.cards.addEventListener('scroll', function () {
    window.clearTimeout(self.cardTimer);
    self.cardTimer = window.setTimeout(sync, 60);
  }, { passive: true });

  dots.forEach(function (d, i) {
    d.addEventListener('click', function () {
      // horizontal only: scrollIntoView here would scroll the page as well
      self.scrollXTo(self.cards, items[i]);
    });
  });

  window.addEventListener('resize', sync);
  sync();
};

/* ---- pinned layout ------------------------------------------------------
   The tall wrapper is the scroll track and the frame inside it sticks. One
   rect read per frame, and select() only runs when the index actually moves.
   ------------------------------------------------------------------------ */
CloserLook.prototype.bindPin = function () {
  var self = this;
  if (!this.pin) return;

  var apply = function () {
    if (!self.mobile() || self.mlayout !== 'pinned') return;
    var n = (self.data.features || []).length;
    if (!n) return;
    var r = self.pin.getBoundingClientRect();
    var travel = r.height - window.innerHeight;
    if (travel <= 0) return;
    var p = -r.top / travel;
    if (p < 0) p = 0;
    if (p > 1) p = 1;
    var i = Math.floor(p * n);
    if (i > n - 1) i = n - 1;
    if (i !== self.feature) self.select(i, false);
  };

  window.addEventListener('scroll', function () {
    if (self.pinTick) return;
    self.pinTick = true;
    window.requestAnimationFrame(function () { apply(); self.pinTick = false; });
  }, { passive: true });
  window.addEventListener('resize', apply);
  apply();
};

CloserLook.prototype.bindCollapse = function () {
    var self = this;
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function () { self.collapse(); });
    }
    this.root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (self.viewer && self.viewer.open) return;   // the viewer handles its own Escape
      if (self.root.querySelector('.hp-cl__pill.is-open')) {
        e.stopPropagation();
        self.collapse();
      }
    });
  };

  CloserLook.prototype.collapse = function () {
    var open = this.root.querySelector('.hp-cl__pill.is-open');
    if (!open) return;
    var isColor = open.hasAttribute('data-colors');
    var idx = isColor ? -1 : parseInt(open.getAttribute('data-feature'), 10);
    this.toggle(open, idx, isColor);
    var btn = open.querySelector('.hp-cl__pill-btn');
    // focus without letting the browser scroll the pill into view
    if (btn) btn.focus({ preventScroll: true });
  };

  CloserLook.prototype.syncClose = function () {
    if (!this.closeBtn) return;
    this.closeBtn.hidden = !this.root.querySelector('.hp-cl__pill.is-open');
  };

  /* ---- takeover --------------------------------------------------------- */
  /* The component lives in one place in the DOM and is physically moved into
     the dialog, so state, listeners and the open pill all survive the switch. */
  CloserLook.prototype.bindTakeover = function () {
    var self = this;
    if (!this.takeover || !this.dialogHost || !this.inlineHost) return;

    // where the component lives when it is not in the takeover
    this.homeParent = this.inlineHost.parentNode;
    this.homeNext = this.inlineHost.nextSibling;

    var opener = this.root.querySelector('[data-cl-open]');
    if (opener) {
      opener.addEventListener('click', function () {
        self.dialogHost.appendChild(self.inlineHost);
        self.takeoverOpener = opener;
        if (self.takeover.showModal) self.takeover.showModal();
        else self.takeover.setAttribute('open', '');
      });
    }

    var closer = this.takeover.querySelector('[data-cl-close]');
    if (closer) {
      closer.addEventListener('click', function () {
        if (self.takeover.close) self.takeover.close();
        else self.takeover.removeAttribute('open');
        self.restore();
      });
    }

    // Restore from both paths. The dialog close event is the reliable one for
    // Escape and backdrop dismissal; the button calls it directly too so the
    // component never gets stranded inside the dialog.
    // The dialog close event is unreliable across browsers, but every
    // dismissal path (button, Escape, programmatic close) toggles the open
    // attribute, so watch that instead.
    this.takeover.addEventListener('close', function () { self.restore(); });
    this.takeover.addEventListener('cancel', function () {
      window.setTimeout(function () { self.restore(); }, 0);
    });
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (!self.takeover.hasAttribute('open')) self.restore();
      }).observe(this.takeover, { attributes: true, attributeFilter: ['open'] });
    }

    // takeover-only: open it straight away if the merchant hid the inline view
    if (this.mode === 'takeover' && opener) opener.setAttribute('data-cl-primary', 'true');
  };

  CloserLook.prototype.restore = function () {
    if (!this.inlineHost || !this.homeParent) return;
    if (this.inlineHost.parentNode === this.homeParent) return; // already home
    this.homeParent.insertBefore(this.inlineHost, this.homeNext);
    // returning focus must not scroll the page to the opener
    if (this.takeoverOpener && this.takeoverOpener.focus) this.takeoverOpener.focus({ preventScroll: true });
  };

  /* ---- desktop expand --------------------------------------------------- */
  /* Single-open, owned here. The theme's Accordion animates the height but
     never exposes an open state, so it cannot drive one-at-a-time behaviour
     or keep aria-expanded in sync. */
  CloserLook.prototype.toggle = function (pill, idx, isColor) {
    var open = !pill.classList.contains('is-open');

    this.pills.forEach(function (p) {
      var b = p.querySelector('.hp-cl__pill-btn');
      var o = p === pill && open;
      p.classList.toggle('is-open', o);
      if (b) b.setAttribute('aria-expanded', o ? 'true' : 'false');
    });

    this.feature = open && !isColor ? idx : -1;
    if (this.stepper) this.stepper.hidden = this.feature < 0;
    this.syncClose();
    this.render();
  };

  CloserLook.prototype.step = function (dir) {
    if (!this.featurePills.length) return;
    var n = this.feature < 0 ? 0 : this.feature + dir;
    if (n < 0) n = this.featurePills.length - 1;
    if (n > this.featurePills.length - 1) n = 0;
    var btn = this.featurePills[n].querySelector('.hp-cl__pill-btn');
    if (btn) btn.click();
  };

  /* ---- colour ----------------------------------------------------------- */
  CloserLook.prototype.setColor = function (i, silent) {
    var c = this.data.colors[i];
    if (!c) return;
    this.color = i;
    this.swatches.forEach(function (sw, n) {
      sw.setAttribute('aria-checked', n === i ? 'true' : 'false');
      sw.setAttribute('tabindex', n === i ? '0' : '-1');
    });
    if (this.dot) this.dot.style.setProperty('--cl-dot', c.swatch);
    if (this.colorText) this.colorText.innerHTML = '<strong>' + c.textTitle + '</strong> ' + c.text;
    if (this.feature < 0) this.paintMText();
    this.render(silent);
  };

  /* ---- image resolution + crossfade ------------------------------------- */
  CloserLook.prototype.srcFor = function (featureIndex, colorIndex) {
    var small = this.mobile();
    var f = featureIndex >= 0 ? this.data.features[featureIndex] : null;
    if (f) {
      if (small && f.imageMobile) return f.imageMobile;
      if (f.images && f.images[colorIndex]) return f.images[colorIndex];
      if (f.image) return f.image;
    }
    var c = this.data.colors[colorIndex];
    if (!c) return '';
    if (small && c.imageMobile) return c.imageMobile;
    return c.image || '';
  };

  /* Whichever item is showing decides the image placement, falling back to
     the section setting when the block says "inherit". */
  CloserLook.prototype.stageMode = function () {
    var item = this.feature >= 0 ? this.data.features[this.feature] : this.data.colors[this.color];
    var m = item && item.layout;
    if (!m || m === 'inherit') m = this.defaultStage;
    return m;
  };

  CloserLook.prototype.render = function (silent) {
    this.root.setAttribute('data-stage', this.stageMode());
    var src = this.srcFor(this.feature, this.color);
    if (!src || this.imgs.length < 2) return;
    if (src === this.currentSrc) return;
    this.currentSrc = src;

    var next = this.imgs[this.buffer ? 0 : 1];
    var prev = this.imgs[this.buffer];
    var self = this;

    var done = false;
    var show = function () {
      if (done) return;
      done = true;
      next.classList.add('is-active');
      prev.classList.remove('is-active');
      self.buffer = self.buffer ? 0 : 1;
    };

    next.alt = this.altFor();
    if (silent) { next.src = src; show(); return; }

    next.onload = show;
    next.onerror = show;
    next.src = src;
    // a cached image may already be complete, and a stalled request must not
    // leave the stage showing the previous colour for ever
    if (next.complete) show();
    window.setTimeout(show, 700);
  };

  CloserLook.prototype.altFor = function () {
    var f = this.feature >= 0 ? this.data.features[this.feature] : null;
    var c = this.data.colors[this.color];
    return (f ? f.title + ' - ' : '') + (c ? c.label : '');
  };

  /* ---- mobile viewer ---------------------------------------------------- */
  CloserLook.prototype.viewerSlotFor = function (isColor, featureIndex) {
    for (var i = 0; i < this.viewerItems.length; i++) {
      var it = this.viewerItems[i];
      if (isColor && it.type === 'colors') return i;
      if (!isColor && it.type === 'feature' && it.index === featureIndex) return i;
    }
    return 0;
  };

  CloserLook.prototype.openViewer = function (i) {
    if (!this.viewer) return;
    this.viewerIndex = i;
    var pill = this.viewerItems[i] && this.viewerItems[i].type === 'colors'
      ? this.root.querySelector('.hp-cl__pill[data-colors]')
      : this.featurePills[this.viewerItems[i] ? this.viewerItems[i].index : 0];
    this.viewerOpener = pill ? pill.querySelector('.hp-cl__pill-btn') : null;
    this.paintViewer();
    if (this.viewer.showModal) this.viewer.showModal();
    else this.viewer.setAttribute('open', '');
  };

  CloserLook.prototype.paintViewer = function () {
    var self = this;
    var item = this.viewerItems[this.viewerIndex];
    if (!item) return;
    var img = this.viewer.querySelector('.hp-cl__viewer-media img');
    var text = this.viewer.querySelector('.hp-cl__viewer-text');
    var box = this.viewer.querySelector('.hp-cl__viewer-swatches');

    if (item.type === 'colors') {
      var c = this.data.colors[this.color] || {};
      if (img) { img.src = this.srcFor(-1, this.color); img.alt = c.label || ''; }
      if (text) text.innerHTML = '<strong>' + (c.textTitle || '') + '</strong> ' + (c.text || '');
      if (box) {
        box.hidden = false;
        if (!box.childNodes.length) {
          this.data.colors.forEach(function (col, i) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'hp-cl__viewer-swatch';
            b.setAttribute('role', 'radio');
            b.setAttribute('aria-label', (self.root.getAttribute('data-swatch-prefix') || '') + ' ' + (col.label || ''));
            b.style.setProperty('--sw', col.swatch);
            b.addEventListener('click', function () { self.setColor(i); self.paintViewer(); });
            box.appendChild(b);
          });
          box.setAttribute('role', 'radiogroup');
        }
        [].slice.call(box.children).forEach(function (b, i) {
          b.setAttribute('aria-checked', i === self.color ? 'true' : 'false');
        });
      }
      return;
    }

    var f = this.data.features[item.index];
    if (!f) return;
    if (img) { img.src = this.srcFor(item.index, this.color); img.alt = f.title; }
    if (text) text.innerHTML = '<strong>' + f.title + '</strong> ' + f.text;
    if (box) box.hidden = true;
  };

  CloserLook.prototype.viewerStep = function (dir) {
    var n = this.viewerIndex + dir;
    if (n < 0) n = this.viewerItems.length - 1;
    if (n > this.viewerItems.length - 1) n = 0;
    this.viewerIndex = n;
    this.paintViewer();
  };

  CloserLook.prototype.closeViewer = function () {
    if (!this.viewer) return;
    if (this.viewer.close) this.viewer.close();
    else this.viewer.removeAttribute('open');
  };

  /* ---- boot ------------------------------------------------------------- */
  function boot(scope) {
    var nodes = (scope || document).querySelectorAll('.hp-cl');
    [].slice.call(nodes).forEach(function (el) {
      if (el.hpCloserLook) return;
      el.hpCloserLook = new CloserLook(el);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else { boot(); }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
